// SUB-OFFICE 7 — the DOM overlay (the print layer's thin edge for this slice):
// the bottom context line (typeset like a form footer, printed letter by
// letter), the aria-live chyron mirror (BUFFERS AND ANNOUNCES WHOLE LINES,
// never per-letter), and toasts in the Registrar's voice.

import type { EventBus } from '../entities/entities';

const PRINT_CPS = 55; // context line print rate, chars/second

export class DomUi {
  private contextEl: HTMLElement;
  private mirrorEl: HTMLElement;
  private toastsEl: HTMLElement;

  private prompt: string | null = null;
  private sticky: string | null = null;
  private stickyLeft = 0;
  private printing = '';
  private printedChars = 0;
  private printAcc = 0;
  private shown = '';
  private reduced: boolean;

  constructor(bus: EventBus, reducedMotion: boolean) {
    this.reduced = reducedMotion;
    this.contextEl = document.getElementById('context') as HTMLElement;
    this.mirrorEl = document.getElementById('chyron-mirror') as HTMLElement;
    this.toastsEl = document.getElementById('toasts') as HTMLElement;

    bus.on('chyron:line', (p) => this.mirror(p as string));
    bus.on('toast', (p) => this.toast(p as string));
    bus.on('context', (p) => {
      const t = p as string | null;
      if (t) this.say(t);
    });
  }

  /** Low-priority proximity/hover prompt; instant, replaced freely. */
  setPrompt(text: string | null): void {
    this.prompt = text;
  }

  /** Sticky line: letter-printed, holds ~4s, then yields to the prompt. */
  say(text: string, holdS = 4): void {
    this.sticky = text;
    this.stickyLeft = holdS;
    this.printing = text;
    this.printedChars = this.reduced ? text.length : 0;
    this.printAcc = 0;
  }

  update(dt: number): void {
    if (this.sticky) {
      this.stickyLeft -= dt;
      if (this.printedChars < this.printing.length) {
        this.printAcc += dt * PRINT_CPS;
        const step = Math.floor(this.printAcc);
        if (step > 0) {
          this.printedChars = Math.min(this.printing.length, this.printedChars + step);
          this.printAcc -= step;
        }
      }
      if (this.stickyLeft <= 0) this.sticky = null;
    }
    const want = this.sticky ? this.printing.slice(0, this.printedChars) : (this.prompt ?? '');
    if (want !== this.shown) {
      this.shown = want;
      this.contextEl.textContent = want;
    }
  }

  // -------------------------------------------------------------------------
  // Docket strip — the session's five slots, pinned like a work order.
  // (Styles ship with the print-layer stylesheet in ui/chrome.ts.)
  // -------------------------------------------------------------------------

  private strip: HTMLElement | null = null;

  private ensureStrip(): HTMLElement {
    if (!this.strip) {
      this.strip = document.createElement('div');
      this.strip.id = 'docket-strip';
      document.body.appendChild(this.strip);
    }
    return this.strip;
  }

  /** Render the strip. rows: label + PENDING/DONE/ACTIVE; banner: e.g. the
   *  INDUSTRIAL ACTION line. Pass null rows to hide the strip entirely. */
  setDocket(
    rows: { label: string; status: 'PENDING' | 'DONE' | 'ACTIVE' }[] | null,
    banner: string | null = null
  ): void {
    const el = this.ensureStrip();
    if (!rows) {
      el.style.display = 'none';
      return;
    }
    el.style.display = '';
    el.innerHTML = '<div class="ds-head">DOCKET — SUB-OFFICE 7</div>';
    for (const r of rows) {
      const row = document.createElement('div');
      row.className = 'ds-row' + (r.status === 'DONE' ? ' done' : r.status === 'ACTIVE' ? ' active' : '');
      const box = document.createElement('span');
      box.className = 'box';
      box.textContent = r.status === 'DONE' ? '■' : r.status === 'ACTIVE' ? '▶' : '□';
      const label = document.createElement('span');
      label.textContent = r.label;
      row.append(box, label);
      el.appendChild(row);
    }
    if (banner) {
      const b = document.createElement('div');
      b.className = 'ds-banner';
      b.textContent = banner;
      el.appendChild(b);
    }
  }

  /** DOCKET CLEARED — the stamp slams across the board. */
  showDocketCleared(): void {
    const el = this.ensureStrip();
    const s = document.createElement('div');
    s.className = 'ds-cleared';
    s.textContent = 'DOCKET CLEARED +2';
    el.appendChild(s);
  }

  /** aria-live mirror: whole lines only; keeps the last 4 announcements. */
  private mirror(line: string): void {
    const div = document.createElement('div');
    div.textContent = line;
    this.mirrorEl.appendChild(div);
    while (this.mirrorEl.childNodes.length > 4) {
      this.mirrorEl.removeChild(this.mirrorEl.firstChild as Node);
    }
  }

  toast(text: string, ms = 5200): void {
    const div = document.createElement('div');
    div.className = 'toast typewriter';
    div.textContent = text;
    this.toastsEl.appendChild(div);
    window.setTimeout(() => div.remove(), ms);
  }
}
